/* eslint-disable max-len */
/**
 * @fileoverview Compact Post Card for Queue & History.
 *               PATH: src/components/common/DashboardPostCard.js
 *               Horizontal layout: small visual left, caption + actions right.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FiFacebook, FiInstagram, FiEdit2, FiTrash2, FiZap,
    FiCheckCircle, FiClock, FiAlertCircle, FiMoon, FiRefreshCw, FiLayers,
    FiChevronLeft, FiChevronRight, FiImage,
    FiEye, FiHeart, FiMessageCircle, FiShare2,
    FiMoreVertical, FiSend
} from 'react-icons/fi';
import { FaLinkedin } from 'react-icons/fa';
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from '../../firebaseConfig';

const PLATFORM_ORDER = ['facebook', 'instagram', 'linkedin'];

const StatusPill = ({ status }) => {
    const config = {
        posted: { style: "bg-pastel-green-bg/30 text-pastel-green border-pastel-green/30", icon: <FiCheckCircle size={10} />, label: "Published" },
        ready: { style: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: <FiZap size={10} />, label: "Ready" },
        queued: { style: "bg-rosy-taupe/10 text-rosy-taupe border-rosy-taupe/20", icon: <FiMoon size={10} />, label: "Generating" },
        draft: { style: "bg-white/5 text-white/50 border-white/10", icon: <FiClock size={10} />, label: "Draft" },
        failed: { style: "bg-red-500/10 text-red-400 border-red-500/20", icon: <FiAlertCircle size={10} />, label: "Failed" },
        cancelled: { style: "bg-white/5 text-white/30 border-white/5", icon: <FiTrash2 size={10} />, label: "Cancelled" }
    };
    const c = config[status] || config.draft;
    return (
        <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wider ${c.style}`}>
            {c.icon}{c.label}
        </span>
    );
};

const PlatformIcon = ({ platform, size = 12 }) => {
    if (platform === 'facebook') return <FiFacebook size={size} className="text-blue-400" />;
    if (platform === 'instagram') return <FiInstagram size={size} className="text-pink-400" />;
    if (platform === 'linkedin') return <FaLinkedin size={size} className="text-[#0077B5]" />;
    return null;
};

const DashboardPostCard = ({
    post, viewMode, onEdit, onDelete, onPostNow, onRetry, loadingAction
}) => {
    const isMultiPlatform = typeof post.content === 'object' && post.content !== null;
    const activePlatforms = isMultiPlatform
        ? PLATFORM_ORDER.filter(p => post.content[p])
        : [post.platform?.toLowerCase() || 'facebook'];

    const isCarouselPost = activePlatforms.some(p => (post.adjustments?.templates?.[p] || '').includes('carousel'));
    const isMergedView = isMultiPlatform && activePlatforms.length > 1 && !isCarouselPost;

    const [slideIndices, setSlideIndices] = React.useState({});
    const [imageLoadErrors, setImageLoadErrors] = React.useState({});
    const [freshUrls, setFreshUrls] = React.useState({});
    const [captionPlatform, setCaptionPlatform] = React.useState(activePlatforms[0]);
    const [menuOpen, setMenuOpen] = React.useState(false);
    const menuRef = React.useRef(null);

    // Close menu on outside click
    React.useEffect(() => {
        if (!menuOpen) return;
        const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    const handleRecoverImage = React.useCallback(async (platform, storagePath, slideIndex) => {
        const key = slideIndex != null ? `${platform}_${slideIndex}` : platform;
        if (!storagePath) { setImageLoadErrors(prev => ({...prev, [key]: true})); return; }
        try {
            const freshUrl = await getDownloadURL(ref(storage, storagePath));
            setFreshUrls(prev => ({...prev, [key]: freshUrl}));
            setImageLoadErrors(prev => ({...prev, [key]: false}));
        } catch { setImageLoadErrors(prev => ({...prev, [key]: true})); }
    }, []);

    const handleSlideChange = (e, platform, direction, totalSlides) => {
        e.stopPropagation();
        setSlideIndices(prev => {
            const cur = prev[platform] || 0;
            let n = direction === 'next' ? cur + 1 : cur - 1;
            if (n >= totalSlides) n = 0; if (n < 0) n = totalSlides - 1;
            return { ...prev, [platform]: n };
        });
    };

    const getVisualData = (platform) => {
        if (!post.visual) return null;
        if (post.visual[platform]?.url) return post.visual[platform];
        if (post.visual.url) return post.visual;
        return post.visual[platform] || null;
    };

    const getPlatformStatus = (platform) => {
        if (post.status === 'cancelled') return 'cancelled';
        if (viewMode === 'history') {
            const ids = post.platformPostId || "";
            return ids.includes(platform) ? 'posted' : 'failed';
        }
        const tmpl = post.adjustments?.templates?.[platform] || "standard";
        if (tmpl.includes("carousel")) {
            const seq = post.visualSequence?.[platform] || [];
            if (seq.length === 0) return 'draft';
            if (seq.every(s => s.status === 'ready' && s.visual?.url)) return 'ready';
            return seq.some(s => s.status === 'queued') ? 'queued' : 'draft';
        }
        const v = getVisualData(platform);
        if (v?.status === 'queued') return 'queued';
        if (v?.url) return 'ready';
        return 'draft';
    };

    // Use first platform for visual display (merged or single)
    const displayPlatform = isMergedView ? activePlatforms[0] : activePlatforms[0];
    const displayStatus = getPlatformStatus(displayPlatform);

    // Render the card for each platform section (or one merged)
    const platformsToRender = isMergedView ? [activePlatforms[0]] : activePlatforms;

    // Get timestamp display
    const getTimeDisplay = () => {
        const ts = post.status === 'posted' ? (post.postedAt || post.updatedAt) : post.status === 'scheduled' ? (post.scheduledTime || post.createdAt) : post.createdAt;
        if (!ts?.toDate) return 'Date pending';
        const d = new Date(ts.toDate());
        return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    };

    return (
        <div className="bg-warm-gray/20 border border-white/5 rounded-xl shadow-card overflow-hidden hover:border-slate-blue/20 transition-all group/card">
            {platformsToRender.map((platform, idx) => {
                const template = post.adjustments?.templates?.[platform] || "standard";
                const isCarousel = template.includes("carousel");
                const status = getPlatformStatus(platform);

                let visualUrl = null;
                let slideCount = 0;

                if (isCarousel) {
                    const sequence = post.visualSequence?.[platform] || [];
                    slideCount = sequence.length;
                    const curIdx = slideIndices[platform] || 0;
                    const slide = sequence[curIdx] || sequence[0];
                    visualUrl = freshUrls[`${platform}_${curIdx}`] || ((slide?.visual?.url?.startsWith('http')) ? slide.visual.url : null);
                } else {
                    const v = getVisualData(platform);
                    visualUrl = freshUrls[platform] || ((v?.url?.startsWith('http')) ? v.url : null);
                }

                const currentVisualObj = isCarousel
                    ? ((post.visualSequence?.[platform] || [])[slideIndices[platform] || 0]?.visual)
                    : getVisualData(platform);
                const isVideo = currentVisualObj?.type === 'video' || currentVisualObj?.metadata?.contentType === 'video/mp4';

                const content = isMergedView
                    ? (post.content[captionPlatform] || post.content[platform])
                    : (isMultiPlatform ? post.content[platform] : post.content);

                const insightsPlatform = isMergedView ? captionPlatform : platform;
                const insights = post.insights?.[insightsPlatform];

                return (
                    <div key={platform} className={`${idx > 0 ? 'border-t border-white/5' : ''}`}>
                        {/* Header row: platform + status + actions */}
                        <div className="px-4 py-2.5 flex items-center justify-between bg-black/10">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                {isMergedView
                                    ? activePlatforms.map(p => <PlatformIcon key={p} platform={p} />)
                                    : <PlatformIcon platform={platform} />
                                }
                                <span className="text-[11px] font-black text-white/60 uppercase tracking-tight truncate">
                                    {isMergedView ? activePlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ') : platform}
                                </span>
                                <StatusPill status={status} />
                            </div>
                            <div className="flex items-center gap-1 ml-2 relative" ref={menuRef}>
                                {/* Primary action */}
                                {viewMode === 'queue' && status === 'ready' && (
                                    <button onClick={() => onPostNow(post)} disabled={loadingAction} className="flex items-center gap-1 px-2.5 py-1 bg-slate-blue/20 hover:bg-slate-blue/30 text-slate-blue border border-slate-blue/30 rounded-lg text-[9px] font-bold uppercase transition-all disabled:opacity-50" title="Publish now">
                                        {loadingAction ? <FiRefreshCw className="animate-spin" size={10} /> : <FiSend size={10} />} Publish
                                    </button>
                                )}
                                {status === 'failed' && onRetry && (
                                    <button onClick={() => onRetry(post, platform)} className="flex items-center gap-1 px-2.5 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-[9px] font-bold uppercase transition-all">
                                        <FiRefreshCw size={10} /> Re-publish
                                    </button>
                                )}
                                {/* Menu */}
                                <button onClick={() => setMenuOpen(!menuOpen)} className="p-1.5 text-white/30 hover:text-white/60 hover:bg-white/5 rounded-lg transition-all">
                                    <FiMoreVertical size={14} />
                                </button>
                                {menuOpen && (
                                    <div className="absolute right-0 top-full mt-1 z-50 bg-warm-dark border border-white/10 rounded-xl shadow-2xl py-1 min-w-[140px]">
                                        <button onClick={() => { onEdit(post, isMergedView ? captionPlatform : platform); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-cream/70 hover:text-cream hover:bg-white/5 transition-all">
                                            <FiEdit2 size={12} /> Edit Post
                                        </button>
                                        <div className="border-t border-white/5 my-1"></div>
                                        <button onClick={() => { onDelete(post); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-bold text-red-400 hover:bg-red-500/10 transition-all">
                                            <FiTrash2 size={12} /> Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Content row: visual thumbnail + caption */}
                        <div className="px-4 py-3 flex gap-4">
                            {/* Visual thumbnail */}
                            <div className="relative flex-shrink-0 w-20 h-24 bg-warm-dark rounded-lg overflow-hidden border border-white/5 group cursor-pointer" onClick={() => onEdit(post, isMergedView ? captionPlatform : platform)}>
                                {visualUrl && !(isCarousel ? imageLoadErrors[`${platform}_${slideIndices[platform] || 0}`] : imageLoadErrors[platform]) ? (
                                    isVideo ? (
                                        <video key={visualUrl} className="w-full h-full object-cover" muted><source src={visualUrl} type={currentVisualObj?.metadata?.contentType || 'video/mp4'} /></video>
                                    ) : (
                                        <img key={visualUrl} src={visualUrl} alt="" className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                const curSlideIdx = slideIndices[platform] || 0;
                                                let recoveryPath = null;
                                                if (isCarousel) {
                                                    recoveryPath = (post.visualSequence?.[platform] || [])[curSlideIdx]?.visual?.storagePath;
                                                } else {
                                                    const v = getVisualData(platform);
                                                    recoveryPath = v?.storagePath || v?.metadata?.storagePath || post.visual?.storagePath;
                                                }
                                                if (recoveryPath) handleRecoverImage(platform, recoveryPath, isCarousel ? curSlideIdx : undefined);
                                                else setImageLoadErrors(prev => ({...prev, [isCarousel ? `${platform}_${curSlideIdx}` : platform]: true}));
                                            }}
                                        />
                                    )
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-white/20">
                                        <FiImage size={18} />
                                    </div>
                                )}
                                {isCarousel && slideCount > 0 && (
                                    <div className="absolute bottom-1 right-1 bg-black/70 text-white px-1 py-0.5 rounded text-[7px] font-bold flex items-center gap-0.5">
                                        <FiLayers size={7} /> {(slideIndices[platform] || 0) + 1}/{slideCount}
                                    </div>
                                )}
                                {isVideo && (
                                    <div className="absolute top-1 left-1 bg-black/70 text-white px-1 py-0.5 rounded text-[7px] font-bold uppercase">Video</div>
                                )}
                            </div>

                            {/* Caption + metadata */}
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-xs font-bold text-white/80 truncate mb-1" title={post.subject}>
                                        {post.subject || "Untitled"}
                                    </h4>
                                    <p className="text-[11px] text-white/40 line-clamp-2 leading-relaxed text-left">
                                        {content || <span className="italic">No caption yet</span>}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between mt-2">
                                    <span className="text-[9px] text-white/25 font-bold uppercase tracking-widest flex items-center gap-1">
                                        <FiClock size={9} /> {getTimeDisplay()}
                                    </span>

                                    {/* Caption platform tabs (merged) */}
                                    {isMergedView && (
                                        <div className="flex gap-0.5 bg-black/20 rounded p-0.5">
                                            {activePlatforms.map(p => (
                                                <button key={p} onClick={() => setCaptionPlatform(p)}
                                                    className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase transition-all ${captionPlatform === p ? 'bg-slate-blue text-warm-dark' : 'text-cream/30 hover:text-cream/50'}`}
                                                >
                                                    {p === 'facebook' ? 'FB' : p === 'instagram' ? 'IG' : 'LI'}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Insights row (history, posted only) */}
                        {viewMode === 'history' && status === 'posted' && insights && (
                            <div className="px-4 pb-3">
                                <div className="flex items-center gap-4 px-3 py-1.5 bg-black/20 rounded-lg text-[9px] font-bold uppercase tracking-widest text-cream/40">
                                    <span className="flex items-center gap-1"><FiEye size={9} /> {insights.views || 0}</span>
                                    <span className="flex items-center gap-1"><FiHeart size={9} /> {insights.likes || 0}</span>
                                    <span className="flex items-center gap-1"><FiMessageCircle size={9} /> {insights.comments || 0}</span>
                                    <span className="flex items-center gap-1"><FiShare2 size={9} /> {insights.shares || 0}</span>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

DashboardPostCard.propTypes = {
    post: PropTypes.object.isRequired,
    viewMode: PropTypes.oneOf(['queue', 'history']).isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onPostNow: PropTypes.func,
    onRetry: PropTypes.func,
    loadingAction: PropTypes.bool
};

export default DashboardPostCard;
